import Image from 'next/image'

import { useCountersList } from '@/api/models/counters'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import { AppButton } from '@/components/shared/button'
import { CardSkeleton } from '@/components/shared/skeleton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { useCountersAvailability } from '../hooks/useCountersAvailability'
import CountersSummaryDescription from './CountersSummaryDescription'

interface CountersSummaryProps {
  balancesLength?: number
  countersContentClassName?: string
  alignImageToBottom?: boolean
}

const CountersSummary = ({
  balancesLength,
  countersContentClassName,
  alignImageToBottom = false
}: CountersSummaryProps) => {
  const { reachGoal } = useYandexMetrika()

  const { breakpoint } = useCurrentBreakpoint()
  const isDesktopScreen = [ScreenSize.Xl, ScreenSize.Xxl].includes(breakpoint)
  const isTabletScreen = [ScreenSize.Md, ScreenSize.Lg].includes(breakpoint)
  const isSmallScreen = [ScreenSize.Xxs, ScreenSize.Xs, ScreenSize.Sm].includes(
    breakpoint
  )
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)

  const {
    data: countersList,
    isLoading,
    isError,
    error: countersListError
  } = useCountersList({
    onlyAvailable: false
  })

  const areCountersAvailableNow = useCountersAvailability(countersList)

  if (isForbiddenError(countersListError)) {
    return (
      <div className="rounded-16 bg-white p-16 lg:p-16">
        <ErrorForbidden className="mb-[20px] mt-[20px] sm:mb-[16px] sm:mt-[16px]" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-16 bg-white p-20 lg:p-16">
        <ErrorCard />
      </div>
    )
  }

  if (isLoading) {
    return (
      <CardSkeleton className="h-full min-h-[80px] w-full sm:min-h-[187px]" />
    )
  }

  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden rounded-16 bg-white p-20 transition-[height] duration-300 ease-in-out md:p-16 sm:p-16'
      )}
    >
      <div
        className={`${countersContentClassName} flex h-full sm:h-[187px] sm:flex-col sm:items-start`}
      >
        <div className="z-10 flex-1">
          <p className="font-bold text-grey-100 text-28 md:text-24 sm:text-20">
            Приборы учёта
          </p>
          <p
            className={cn(
              'relative mt-4 block w-[200px] font-pro !text-grey-70 text-16 lg:text-14',
              balancesLength === 2 && isTabletScreen && 'w-full',
              isMobileScreen && 'w-[260px]'
            )}
          >
            <CountersSummaryDescription
              countersList={countersList || []}
              areCountersAvailableNow={areCountersAvailableNow || false}
            />
          </p>
        </div>
        {areCountersAvailableNow && (
          <AppButton
            variant="secondary"
            className={cn(
              'z-10 !h-[48px] w-full max-w-[190px] whitespace-nowrap sm:max-w-full',
              balancesLength === 1 &&
                (isTabletScreen || isSmallScreen) &&
                'max-w-full'
            )}
            href="/counters"
            onClick={() => reachGoal(YandexMetrikaEvent.MAIN_COUNTERS_LIST)}
            size="small"
          >
            Передать показания
          </AppButton>
        )}
      </div>

      <div
        className={cn(
          'absolute object-contain',
          'h-[450px] w-[450px] -rotate-2',
          (balancesLength === 3 || balancesLength === 0) &&
            (isDesktopScreen || isTabletScreen) &&
            'h-[250px] w-[250px] ',
          {
            '-bottom-[210px] right-[40px] scale-[0.7]':
              balancesLength === 2 && isTabletScreen
          },
          { 'right-[-105px] rotate-1': isDesktopScreen },
          { '-top-50': isDesktopScreen && !alignImageToBottom },
          { '-bottom-[132px]': isDesktopScreen && alignImageToBottom },
          {
            '-right-75': isTabletScreen && balancesLength !== 2
          },
          {
            '-top-50':
              isTabletScreen && balancesLength !== 2 && !alignImageToBottom
          },
          {
            '-bottom-[200px]':
              isTabletScreen && balancesLength !== 2 && alignImageToBottom
          },
          { '-top-20 right-[-135px]': isSmallScreen },
          {
            '-bottom-[150px] right-0':
              (isDesktopScreen || isTabletScreen) &&
              (balancesLength === 3 || balancesLength === 0)
          }
        )}
      >
        <Image
          width={isSmallScreen ? 340 : 450}
          height={isSmallScreen ? 340 : 450}
          alt="image"
          src="/images/counters/graphs.png"
          priority={true}
        />
      </div>
    </div>
  )
}

export default CountersSummary
